import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useLocation } from 'wouter';
import { format } from 'date-fns';
import { Mail, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useInstallationFieldUpdate } from '@/hooks/use-installation-field-update';
import {
  InlineTextField,
  InlineSelectField,
  InlineDateField,
} from '@/components/ui/inline-edit';
import type { Installation } from '@shared/types/installation';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { InstallationChecklist } from './installation-checklist';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { BatterySerialNumbers } from './battery-serial-numbers';
import { DocumentViewer } from './document-viewer';
import { CompletionOfWorksRecords } from './completion-of-works-records';
import { apiRequest, queryClient } from '@/lib/queryClient';
import {
  ArrowLeft,
  Edit,
  Trash,
  CheckCircle,
  XCircle,
  CalendarIcon,
  Construction,
  Sparkles,
  Banknote,
  FileText,
  Zap,
  Info,
  ClipboardCheck,
  FileCheck,
  Cpu,
  FileArchive,
  CheckCircle2,
  ImageIcon,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

interface InstallationDetailProps {
  id: number;
}

export function InstallationDetail({ id }: InstallationDetailProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [isSendingHandoverDocs, setIsSendingHandoverDocs] = useState(false);
  const [, navigate] = useLocation();
  const { toast } = useToast();

  // Check if we're only viewing documents (e.g., in the document tab)
  const isDocumentTabActive = activeTab === 'documents';

  // Handler for sending handover documents manually
  const handleSendHandoverDocuments = async (installationId: number) => {
    try {
      setIsSendingHandoverDocs(true);
      const response = await fetch(`/api/handover-documents/${installationId}/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Handover documents sent',
          description: data.message || 'The handover documents have been sent successfully.',
          variant: 'default',
        });
      } else {
        toast({
          title: 'Failed to send handover documents',
          description:
            data.message || data.error || 'There was an error sending the handover documents.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error sending handover documents:', error);
      toast({
        title: 'Error',
        description: 'There was an error sending the handover documents. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSendingHandoverDocs(false);
    }
  };

  // Hook for inline field updates
  const fieldUpdate = useInstallationFieldUpdate();

  const handleFieldUpdate = async (field: string, value: any) => {
    if (!id) return;
    await fieldUpdate.mutateAsync({
      installationId: typeof id === 'string' ? parseInt(id) : id,
      field,
      value,
    });
  };

  // Fetch the installation details with proper typing and improved error handling
  const {
    data: rawInstallation,
    isLoading: isLoadingInstallation,
    isError: isErrorInstallation,
    error: installationError,
  } = useQuery<Installation>({
    queryKey: id ? ['/api/installations', id, isDocumentTabActive ? 'documentViewer' : ''] : [],
    enabled: !!id,
    staleTime: 1000 * 60, // 1 minute
    retry: 2, // Retry failed requests up to 2 times,
    queryFn: async ({ queryKey }) => {
      // If we're just viewing documents, append documentViewer=true to the query
      if (isDocumentTabActive) {
        const endpoint = `/api/installations/${id}?documentViewer=true`;
        const response = await fetch(endpoint);
        if (!response.ok) throw new Error('Failed to fetch installation data');
        return await response.json();
      } else {
        // Use the default queryFn behavior for authenticated queries
        const endpoint = `/api/installations/${id}`;
        const response = await fetch(endpoint);
        if (!response.ok) throw new Error('Failed to fetch installation data');
        return await response.json();
      }
    },
  });

  // Use the raw installation data directly to preserve the exact ID value
  // Then add any missing checklist fields with default values if needed
  const installation = rawInstallation
    ? {
        ...rawInstallation,
        // Add defaults for checklist items only if they're missing
        // Admin checklist items
        checklistBookScaffolding: rawInstallation.checklistBookScaffolding || 'pending',
        checklistPoForScaffold: rawInstallation.checklistPoForScaffold || 'pending',
        checklistLetterScaffoldInstallDate:
          rawInstallation.checklistLetterScaffoldInstallDate || 'pending',
        checklistPvPlannerContractors: rawInstallation.checklistPvPlannerContractors || 'pending',

        // On-site tasks items
        checklistScaffoldRemoved: rawInstallation.checklistScaffoldRemoved || 'pending',
        checklistExportMpanReceived: rawInstallation.checklistExportMpanReceived || 'pending',
        checklistSwitchExportConnect: rawInstallation.checklistSwitchExportConnect || 'pending',
        checklistLaminateSchematic: rawInstallation.checklistLaminateSchematic || 'pending',
        checklistLaminateStartupShutdown:
          rawInstallation.checklistLaminateStartupShutdown || 'pending',
        checklistF17ContractManagement: rawInstallation.checklistF17ContractManagement || 'pending',
        checklistConsolidatedForm: rawInstallation.checklistConsolidatedForm || 'pending',
        checklistDnoCommissioningSent: rawInstallation.checklistDnoCommissioningSent || 'pending',
        checklistEicCompleted: rawInstallation.checklistEicCompleted || 'pending',
        checklistStructuralSurvey: rawInstallation.checklistStructuralSurvey || 'pending',
        checklistShadeReport: rawInstallation.checklistShadeReport || 'pending',
        checklistSolarPvDesignCalculations:
          rawInstallation.checklistSolarPvDesignCalculations || 'pending',

        // Post-installation items
        checklistSendSunsynkApp: rawInstallation.checklistSendSunsynkApp || 'pending',
        checklistAmendmentsProposalInvoice:
          rawInstallation.checklistAmendmentsProposalInvoice || 'pending',
        checklistHiesReturnCompleted: rawInstallation.checklistHiesReturnCompleted || 'pending',
        checklistPartPCertificateCreated:
          rawInstallation.checklistPartPCertificateCreated || 'pending',
        checklistMcsCertification: rawInstallation.checklistMcsCertification || 'pending',
        checklistFinancePhoenixPortal: rawInstallation.checklistFinancePhoenixPortal || 'pending',
        checklistInverterSerialToStock: rawInstallation.checklistInverterSerialToStock || 'pending',

        // Handover items
        checklistFinalPaymentReceived: rawInstallation.checklistFinalPaymentReceived || 'pending',
        checklistMcsCertificateProvided:
          rawInstallation.checklistMcsCertificateProvided || 'pending',
        checklistEicCertificateProvided:
          rawInstallation.checklistEicCertificateProvided || 'pending',
        checklistPartPBuildingRegsCertificate:
          rawInstallation.checklistPartPBuildingRegsCertificate || 'pending',
        checklistOwnerManual: rawInstallation.checklistOwnerManual || 'pending',
        checklistStructuralReport: rawInstallation.checklistStructuralReport || 'pending',
        checklistProposalUpdated: rawInstallation.checklistProposalUpdated || 'pending',
        checklistSchematic: rawInstallation.checklistSchematic || 'pending',
        checklistSolarPanelDetails: rawInstallation.checklistSolarPanelDetails || 'pending',
        checklistSolarPvFormsConsolidated:
          rawInstallation.checklistSolarPvFormsConsolidated || 'pending',
        checklistInverterWarranty: rawInstallation.checklistInverterWarranty || 'pending',
        checklistFinalInvoice: rawInstallation.checklistFinalInvoice || 'pending',
        checklistCompleteOpenSolar: rawInstallation.checklistCompleteOpenSolar || 'pending',

        // Maintaining items
        checklistCustomerSatisfactionSurvey:
          rawInstallation.checklistCustomerSatisfactionSurvey || 'pending',
        checklistDiariseYearCheck: rawInstallation.checklistDiariseYearCheck || 'pending',
        checklistAddSerialNumbersStock: rawInstallation.checklistAddSerialNumbersStock || 'pending',
      }
    : undefined;

  // Use customer data from installation response (already included by API)
  // This avoids an extra network call since GET /api/installations/:id includes customer
  const customer = installation?.customer || null;

  // Fetch the proposal information if available
  const { data: proposal = {}, isLoading: isLoadingProposal } = useQuery<any>({
    queryKey: installation?.proposalId ? ['/api/proposals', installation.proposalId] : [],
    enabled: !!installation?.proposalId,
  });

  // Delete installation mutation
  const deleteInstallation = useMutation({
    mutationFn: async () => {
      return apiRequest('DELETE', `/api/installations/${id}`);
    },
    onSuccess: () => {
      // Invalidate installations query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['/api/installations'] });
      toast({
        title: 'Installation deleted',
        description: 'The installation has been successfully deleted',
        variant: 'default',
      });
      // Navigate back to the installations list
      navigate('/installations');
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: `Failed to delete installation: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Helper function to format dates
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not set';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Fixed Status badge component that properly handles DOM nesting
  // Fixed StatusBadge component with proper DOM structure
  const StatusBadge = ({ status }: { status: string }) => {
    // Define variant and text based on status
    let variant: 'default' | 'secondary' | 'destructive' | 'outline' = 'default';
    let text = status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    let className = '';

    // Set appropriate classes and variants based on status
    switch (status) {
      case 'pending':
        variant = 'secondary';
        text = 'Pending';
        break;
      case 'site_survey':
        className = 'bg-purple-500 hover:bg-purple-600';
        text = 'Site Survey Done';
        break;
      case 'ready_to_schedule':
        className = 'bg-pink-500 hover:bg-pink-600';
        text = 'Ready to Schedule';
        break;
      case 'scheduled':
        text = 'Scheduled';
        break;
      case 'waiting_dno':
        className = 'bg-yellow-500 hover:bg-yellow-600';
        text = 'Waiting DNO Approval';
        break;
      case 'in_progress':
        className = 'bg-orange-500 hover:bg-orange-600';
        text = 'In Progress';
        break;
      case 'needs_attention':
        variant = 'destructive';
        text = 'Needs Attention';
        break;
      case 'completed':
        className = 'bg-green-500 hover:bg-green-600';
        text = 'Completed';
        break;
      case 'cancelled':
        variant = 'destructive';
        text = 'Cancelled';
        break;
    }

    // Return badge with a div wrapper to avoid DOM nesting issues when used in a <p> tag
    return (
      <div className="inline-block">
        {className ? (
          <Badge className={className}>{text}</Badge>
        ) : (
          <Badge variant={variant}>{text}</Badge>
        )}
      </div>
    );
  };

  // Loading state
  if (isLoadingInstallation) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div>
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-40 mt-2" />
          </div>
        </div>

        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-36 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If no installation is found
  if (!installation) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Installation Not Found</CardTitle>
          <CardDescription>
            The installation you're looking for doesn't exist or has been removed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <p className="text-muted-foreground">
              Please check the installation ID and try again, or create a new installation.
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex justify-center">
          <Link href="/installations">
            <Button>View All Installations</Button>
          </Link>
        </CardFooter>
      </Card>
    );
  }

  // Tab navigation component - reusable across all tabs
  const TabNavigation = () => {
    // Define tab order for navigation
    const tabOrder = [
      'overview',
      'checklist',
      'payments',
      'technical',
      'documents',
      'completion-records',
    ];

    // Get current tab index and calculate previous/next tabs
    const currentTabIndex = tabOrder.indexOf(activeTab);
    const prevTab = currentTabIndex > 0 ? tabOrder[currentTabIndex - 1] : null;
    const nextTab = currentTabIndex < tabOrder.length - 1 ? tabOrder[currentTabIndex + 1] : null;

    // Get tab labels for display
    const getTabLabel = (tabValue: string) => {
      switch (tabValue) {
        case 'overview':
          return 'Overview';
        case 'checklist':
          return 'Checklist';
        case 'payments':
          return 'Payments';
        case 'technical':
          return 'Technical';
        case 'documents':
          return 'Documents';
        case 'completion-records':
          return 'Completion Records';
        default:
          return tabValue;
      }
    };

    // Get tab icon component
    const getTabIcon = (tabValue: string) => {
      switch (tabValue) {
        case 'overview':
          return <Info className="h-4 w-4" />;
        case 'checklist':
          return <ClipboardCheck className="h-4 w-4" />;
        case 'payments':
          return <Banknote className="h-4 w-4" />;
        case 'technical':
          return <Cpu className="h-4 w-4" />;
        case 'documents':
          return <FileText className="h-4 w-4" />;
        case 'completion-records':
          return <FileCheck className="h-4 w-4" />;
        default:
          return <Info className="h-4 w-4" />;
      }
    };

    return (
      <div className="flex items-center justify-between pt-6 pb-2 border-t mt-8">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2"
            onClick={() => setActiveTab(tabOrder[0])}
            disabled={currentTabIndex === 0}
          >
            <ChevronsLeft className="h-4 w-4 mr-1" />
            First
          </Button>

          {prevTab && (
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setActiveTab(prevTab)}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              {getTabLabel(prevTab)}
            </Button>
          )}
        </div>

        <div className="text-sm text-muted-foreground hidden md:block">
          {currentTabIndex + 1} of {tabOrder.length}
        </div>

        <div className="flex items-center gap-1">
          {nextTab && (
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setActiveTab(nextTab)}
            >
              {getTabLabel(nextTab)}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2"
            onClick={() => setActiveTab(tabOrder[tabOrder.length - 1])}
            disabled={currentTabIndex === tabOrder.length - 1}
          >
            Last
            <ChevronsRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with title, badge and buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link href="/installations">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              Installation #{installation.id}
              <StatusBadge status={installation.status || 'pending'} />
              {/* Quick document access buttons */}
              <div className="ml-4 hidden md:flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setActiveTab('documents')}
                >
                  <FileText className="h-3.5 w-3.5 mr-1" />
                  Docs
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setActiveTab('overview')}
                >
                  <FileText className="h-3.5 w-3.5 mr-1" />
                  Notes
                </Button>
              </div>
            </h1>
            <p className="text-muted-foreground">
              {customer && customer.name} -{' '}
              {installation.installationAddress || 'No address specified'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link href={`/installations/${id}/edit`}>
            <Button variant="outline" className="gap-2">
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          </Link>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="gap-2">
                <Trash className="h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete this installation and all associated milestones. This
                  action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => deleteInstallation.mutate()}
                  disabled={deleteInstallation.isPending}
                >
                  {deleteInstallation.isPending ? 'Deleting...' : 'Delete Installation'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Tabs for different sections */}
      <Tabs
        defaultValue="overview"
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <div className="overflow-x-auto scrollbar-hide">
          <TabsList className="mb-4 grid w-full grid-cols-3 md:grid-cols-6 min-w-[600px]">
            <TabsTrigger value="overview" className="flex items-center gap-1 text-xs sm:text-sm">
              <Info className="h-4 w-4" />
              <span>Overview</span>
            </TabsTrigger>
            <TabsTrigger value="checklist" className="flex items-center gap-1 text-xs sm:text-sm">
              <ClipboardCheck className="h-4 w-4" />
              <span>Checklist</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-1 text-xs sm:text-sm">
              <Banknote className="h-4 w-4" />
              <span>Payments</span>
            </TabsTrigger>
            <TabsTrigger value="technical" className="flex items-center gap-1 text-xs sm:text-sm">
              <Cpu className="h-4 w-4" />
              <span>Technical</span>
            </TabsTrigger>
            <TabsTrigger
              value="documents"
              className="flex items-center gap-1 bg-muted/30 text-xs sm:text-sm"
            >
              <FileText className="h-4 w-4" />
              <span>Documents</span>
            </TabsTrigger>
            <TabsTrigger
              value="completion-records"
              className="flex items-center gap-1 text-xs sm:text-sm"
            >
              <FileCheck className="h-4 w-4" />
              <span>Completion</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Checklist Tab with improved error handling */}
        <TabsContent value="checklist">
          {isErrorInstallation ? (
            <Card>
              <CardContent className="py-8 text-center">
                <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">
                  Error loading installation data:{' '}
                  {(installationError as Error)?.message || 'Unknown error'}
                </p>
                <Button
                  onClick={() =>
                    queryClient.invalidateQueries({ queryKey: ['/api/installations', id] })
                  }
                >
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : installation ? (
            <InstallationChecklist
              key={`checklist-${id}`}
              installation={{
                ...installation,
                // Make sure the ID is explicitly set to the ID from the URL parameter
                id: id,
              }}
            />
          ) : (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground mb-4">
                  Unable to load checklist data. Please try refreshing the page.
                </p>
                <Button
                  onClick={() =>
                    queryClient.invalidateQueries({ queryKey: ['/api/installations', id] })
                  }
                >
                  Refresh Data
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Installation Details
              </CardTitle>
              <CardDescription>Overview of the solar installation project</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Installation Details */}
              <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Customer</h3>
                  <p className="mt-1 text-base">{customer ? customer.name : 'Loading...'}</p>
                  {customer && (
                    <div className="mt-1 text-sm text-muted-foreground">
                      <p>{customer.email}</p>
                      <p>{customer.phone}</p>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">
                    Installation Address
                  </h3>
                  <InlineTextField
                    value={installation.installationAddress}
                    onSave={(value) => handleFieldUpdate('installationAddress', value)}
                    placeholder="Click to add installation address"
                    multiline
                  />
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Proposal</h3>
                  <p className="mt-1 text-base">
                    {proposal ? `Proposal #${proposal.id}` : 'Loading...'}
                  </p>
                  {proposal && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      Created: {formatDate(proposal.createdAt)}
                    </p>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Status</h3>
                  <InlineSelectField
                    value={installation.status || 'loa_mpan'}
                    options={[
                      { value: 'loa_mpan', label: 'LoA & MPAN' },
                      { value: 'g98', label: 'G98' },
                      { value: 'g99', label: 'Waiting DNO (G99)' },
                      { value: 'site_survey', label: 'Site Survey Done' },
                      { value: 'ready_to_schedule', label: 'Ready to Schedule' },
                      { value: 'planning_for_installation', label: 'Planning' },
                      { value: 'prepared_to_install', label: 'Prepared' },
                      { value: 'completed', label: 'Completed' },
                    ]}
                    onSave={(value) => handleFieldUpdate('status', value)}
                  />
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Scheduled Date</h3>
                  <InlineDateField
                    value={installation.scheduledDate ? new Date(installation.scheduledDate) : null}
                    onSave={(value) => handleFieldUpdate('scheduledDate', value)}
                    placeholder="Click to schedule"
                  />
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Completion Date</h3>
                  <InlineDateField
                    value={installation.completedDate ? new Date(installation.completedDate) : null}
                    onSave={(value) => handleFieldUpdate('completedDate', value)}
                    placeholder="Click to set completion date"
                  />
                </div>
              </div>

              {/* System Details */}
              <div>
                <h3 className="text-sm font-medium mb-3">System Details</h3>
                <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">System Type</h4>
                    <InlineTextField
                      value={installation.systemType}
                      onSave={(value) => handleFieldUpdate('systemType', value)}
                      placeholder="Click to add system type"
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">System Size (kW)</h4>
                    <InlineTextField
                      value={installation.systemSize}
                      onSave={(value) => handleFieldUpdate('systemSize', value)}
                      placeholder="Click to add system size"
                      type="number"
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">
                      OpenSolar Reference
                    </h4>
                    <InlineTextField
                      value={installation.openSolarReference}
                      onSave={(value) => handleFieldUpdate('openSolarReference', value)}
                      placeholder="Click to add reference"
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">MPAN Number</h4>
                    <InlineTextField
                      value={installation.mpanNumber}
                      onSave={(value) => handleFieldUpdate('mpanNumber', value)}
                      placeholder="Click to add MPAN"
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Total Cost (£)</h4>
                    <InlineTextField
                      value={installation.totalCost}
                      onSave={(value) => handleFieldUpdate('totalCost', value)}
                      placeholder="Click to add cost"
                      type="number"
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Installer Names</h4>
                    <InlineTextField
                      value={installation.installerNames}
                      onSave={(value) => handleFieldUpdate('installerNames', value)}
                      placeholder="Click to assign installers"
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Panel Model</h4>
                    <InlineTextField
                      value={installation.panelModel}
                      onSave={(value) => handleFieldUpdate('panelModel', value)}
                      placeholder="Click to add panel model"
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Battery Model</h4>
                    <InlineTextField
                      value={installation.batteryModel}
                      onSave={(value) => handleFieldUpdate('batteryModel', value)}
                      placeholder="Click to add battery model"
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Battery Qty</h4>
                    <InlineTextField
                      value={installation.batteryQty}
                      onSave={(value) => handleFieldUpdate('batteryQty', value)}
                      placeholder="Click to add quantity"
                      type="number"
                    />
                  </div>
                </div>
              </div>

              {/* Pre-Install Tracking */}
              <div>
                <h3 className="text-sm font-medium mb-3">Pre-Install Tracking</h3>
                <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Folder Created</h4>
                    <InlineSelectField
                      value={installation.folderCreated ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('folderCreated', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Tradify Added</h4>
                    <InlineSelectField
                      value={installation.tradifyAdded ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('tradifyAdded', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Job Board Added</h4>
                    <InlineSelectField
                      value={installation.jobBoardAdded ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('jobBoardAdded', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Job Board Removed</h4>
                    <InlineSelectField
                      value={installation.jobBoardRemoved ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('jobBoardRemoved', value === 'Yes')}
                    />
                  </div>
                </div>
              </div>

              {/* Scheduling Extended */}
              <div>
                <h3 className="text-sm font-medium mb-3">Scheduling Extended</h3>
                <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Site Visit Date</h4>
                    <InlineDateField
                      value={installation.siteVisitDate ? new Date(installation.siteVisitDate) : null}
                      onSave={(value) => handleFieldUpdate('siteVisitDate', value)}
                      placeholder="Click to set site visit date"
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">7-Day Invoice Issued</h4>
                    <InlineSelectField
                      value={installation.sevenDayInvoiceIssued ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('sevenDayInvoiceIssued', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">RAMS Completed</h4>
                    <InlineSelectField
                      value={installation.ramsCompleted ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('ramsCompleted', value === 'Yes')}
                    />
                  </div>
                </div>
              </div>

              {/* On-Site Tracking */}
              <div>
                <h3 className="text-sm font-medium mb-3">On-Site Tracking</h3>
                <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">EVCP Response</h4>
                    <InlineTextField
                      value={installation.evcpResponse}
                      onSave={(value) => handleFieldUpdate('evcpResponse', value)}
                      placeholder="Click to add EVCP response"
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">On-site Sign-off</h4>
                    <InlineSelectField
                      value={installation.onsiteSignoffCompleted ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('onsiteSignoffCompleted', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">System Alterations</h4>
                    <InlineTextField
                      value={installation.systemAlterations}
                      onSave={(value) => handleFieldUpdate('systemAlterations', value)}
                      placeholder="Click to add alterations"
                      multiline
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Balance Invoice Issued</h4>
                    <InlineSelectField
                      value={installation.balanceInvoiceIssued ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('balanceInvoiceIssued', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Next Steps Email Sent</h4>
                    <InlineSelectField
                      value={installation.nextStepsEmailSent ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('nextStepsEmailSent', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">EVCP Commissioning Sent</h4>
                    <InlineSelectField
                      value={installation.evcpCommissioningSent ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('evcpCommissioningSent', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Maintenance Schedule Provided</h4>
                    <InlineSelectField
                      value={installation.maintenanceScheduleProvided ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('maintenanceScheduleProvided', value === 'Yes')}
                    />
                  </div>
                </div>
              </div>

              {/* Notes - More prominent */}
              <div>
                <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Installation Notes
                </h3>
                <InlineTextField
                  value={installation.notes}
                  onSave={(value) => handleFieldUpdate('notes', value)}
                  placeholder="Click to add notes for this installation"
                  multiline
                  className="w-full min-h-[100px]"
                />
              </div>

              {/* Document preview section for quick access to key documents */}
              <div className="mt-6">
                <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
                  <FileArchive className="h-5 w-5 text-primary" />
                  Key Documents
                  <Button
                    variant="link"
                    size="sm"
                    className="ml-auto"
                    onClick={() => setActiveTab('documents')}
                  >
                    View all documents
                  </Button>
                </h3>
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                  {/* Combine all document arrays and show the most recent 3-6 documents */}
                  {(() => {
                    // Extract all documents from different categories
                    const allDocuments = [
                      ...(installation.technicalDocuments || []),
                      ...(installation.financeDocuments || []),
                      ...(installation.dnoDocuments || []),
                      ...(installation.contractDocuments || []),
                      ...(installation.postInstallDocuments || []),
                      ...(installation.handoverDocuments || []),
                    ].filter((doc) => doc); // Filter out any undefined/null values

                    // If no documents found
                    if (allDocuments.length === 0) {
                      return (
                        <div className="col-span-full p-5 bg-muted/50 rounded-md border border-dashed border-muted-foreground/30 text-center">
                          <p className="text-muted-foreground">
                            No documents have been uploaded yet
                          </p>
                          <Button
                            variant="link"
                            onClick={() => navigate(`/installations/${id}/edit`)}
                          >
                            Upload documents
                          </Button>
                        </div>
                      );
                    }

                    // Display the first few documents (up to 3)
                    return allDocuments.slice(0, 3).map((doc, index) => {
                      // Determine document type for icon
                      let IconComponent = FileText;
                      if (doc.type?.startsWith('image/')) {
                        IconComponent = ImageIcon;
                      }

                      return (
                        <div
                          key={index}
                          className="p-4 border rounded-md bg-white dark:bg-slate-900 hover:border-primary/50 transition-colors cursor-pointer"
                          onClick={() => {
                            // Check if this is an LOA document by looking at multiple indicators
                            const isLoaDocument =
                              (doc as any).filePath?.includes('/letters-of-authority/') ||
                              (doc as any).filePath?.includes('/loa/') ||
                              doc.data?.includes('/letters-of-authority/') ||
                              doc.data?.includes('/loa/') ||
                              doc.data?.startsWith('/loa/') ||
                              (doc as any).isLOA ||
                              (doc as any).loaId ||
                              doc.id?.toString().startsWith('loa-') ||
                              doc.id?.toString().startsWith('loa_') ||
                              doc.name?.toLowerCase().includes('letter_of_authority') ||
                              doc.name?.toLowerCase().includes('letter of authority') ||
                              (doc as any).filename
                                ?.toLowerCase()
                                .includes('letter_of_authority') ||
                              (doc as any).filename
                                ?.toLowerCase()
                                .includes('letter of authority') ||
                              (doc as any).filename?.includes('Letter_of_Authority_') ||
                              (doc as any).category === 'loa';

                            if (isLoaDocument) {
                              // Extract LOA ID from various possible sources
                              let loaId = '';

                              // Direct loaId property (most reliable)
                              if ((doc as any).loaId) {
                                loaId = (doc as any).loaId.toString();
                              }
                              // ID starting with loa-
                              else if (doc.id?.toString().startsWith('loa-')) {
                                loaId = doc.id.toString().replace('loa-', '');
                              }
                              // ID starting with loa_
                              else if (doc.id?.toString().startsWith('loa_')) {
                                loaId = doc.id.toString().replace('loa_', '');
                              }
                              // Extract from filePath like /loa/123
                              else if ((doc as any).filePath?.includes('/loa/')) {
                                const match = (doc as any).filePath.match(/\/loa\/(\d+)/);
                                if (match) {
                                  loaId = match[1];
                                }
                              }
                              // Extract from data URL like /loa/123
                              else if (
                                doc.data?.includes('/loa/') ||
                                doc.data?.startsWith('/loa/')
                              ) {
                                const match = doc.data.match(/\/loa\/(\d+)/);
                                if (match) {
                                  loaId = match[1];
                                }
                              }
                              // Extract from letters-of-authority path
                              else if (doc.data?.includes('/letters-of-authority/')) {
                                const match = doc.data.match(/\/letters-of-authority\/(\d+)/);
                                if (match) {
                                  loaId = match[1];
                                }
                              }
                              // Extract from filePath like /letters-of-authority/123
                              else if ((doc as any).filePath?.includes('/letters-of-authority/')) {
                                const match = (doc as any).filePath.match(
                                  /\/letters-of-authority\/(\d+)/
                                );
                                if (match) {
                                  loaId = match[1];
                                }
                              }
                              // Extract from filename pattern Letter_of_Authority_123
                              else if ((doc as any).filename?.includes('Letter_of_Authority_')) {
                                const match = (doc as any).filename.match(
                                  /Letter_of_Authority_(\d+)/
                                );
                                if (match) {
                                  loaId = match[1];
                                }
                              }
                              // Extract from name pattern
                              else if (doc.name?.includes('Letter of Authority')) {
                                // Try multiple patterns for LOA name
                                const patterns = [
                                  /Letter of Authority.*?-\s*(\d+)/i, // "Letter of Authority - 123"
                                  /Letter of Authority.*?(\d+)/i, // "Letter of Authority 123"
                                  /LOA.*?(\d+)/i, // "LOA 123"
                                  /Authority.*?(\d+)/i, // "Authority 123"
                                ];
                                for (const pattern of patterns) {
                                  const match = doc.name.match(pattern);
                                  if (match) {
                                    loaId = match[1];
                                    break;
                                  }
                                }
                              }

                              if (loaId) {
                                console.log('Opening LOA document with ID:', loaId);
                                navigate(`/loa/${loaId}`);
                                return;
                              } else {
                                console.warn('Could not extract LOA ID from document:', doc);
                              }
                            }

                            // For other documents, open in new tab or navigate to documents tab
                            if (doc.data) {
                              window.open(doc.data, '_blank');
                            } else {
                              setActiveTab('documents');
                            }
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <IconComponent className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                            <div className="overflow-hidden">
                              <p className="font-medium truncate" title={doc.name}>
                                {doc.name || 'Document'}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {doc.type?.split('/')[1]?.toUpperCase() || 'File'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Bottom tab navigation */}
              <TabNavigation />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab content is next */}

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Banknote className="h-5 w-5" />
                Payment Information
              </CardTitle>
              <CardDescription>Track payment details and invoices</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Total Cost (£)</h3>
                  <InlineTextField
                    value={installation.totalCost}
                    onSave={(value) => handleFieldUpdate('totalCost', value)}
                    placeholder="Click to add total cost"
                    type="number"
                  />
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Deposit Amount (£)</h3>
                  <InlineTextField
                    value={installation.depositAmount}
                    onSave={(value) => handleFieldUpdate('depositAmount', value)}
                    placeholder="Click to add deposit amount"
                    type="number"
                  />
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Deposit Paid Date</h3>
                  <InlineDateField
                    value={installation.depositPaid ? new Date(installation.depositPaid) : null}
                    onSave={(value) => handleFieldUpdate('depositPaid', value)}
                    placeholder="Click to set deposit paid date"
                  />
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">7-Day Payment Amount (£)</h3>
                  <InlineTextField
                    value={installation.sevenDayPaymentAmount}
                    onSave={(value) => handleFieldUpdate('sevenDayPaymentAmount', value)}
                    placeholder="Click to add payment amount"
                    type="number"
                  />
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">7-Day Payment Date</h3>
                  <InlineDateField
                    value={installation.sevenDayPaymentDate ? new Date(installation.sevenDayPaymentDate) : null}
                    onSave={(value) => handleFieldUpdate('sevenDayPaymentDate', value)}
                    placeholder="Click to set payment date"
                  />
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Final Payment Amount (£)</h3>
                  <InlineTextField
                    value={installation.finalPaymentAmount}
                    onSave={(value) => handleFieldUpdate('finalPaymentAmount', value)}
                    placeholder="Click to add final payment"
                    type="number"
                  />
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Final Payment Date</h3>
                  <InlineDateField
                    value={installation.finalPaymentDate ? new Date(installation.finalPaymentDate) : null}
                    onSave={(value) => handleFieldUpdate('finalPaymentDate', value)}
                    placeholder="Click to set final payment date"
                  />
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Additional Payment Amount (£)</h3>
                  <InlineTextField
                    value={installation.additionalPaymentAmount}
                    onSave={(value) => handleFieldUpdate('additionalPaymentAmount', value)}
                    placeholder="Click to add additional payment"
                    type="number"
                  />
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Additional Payment Date</h3>
                  <InlineDateField
                    value={installation.additionalPaymentDate ? new Date(installation.additionalPaymentDate) : null}
                    onSave={(value) => handleFieldUpdate('additionalPaymentDate', value)}
                    placeholder="Click to set payment date"
                  />
                </div>
              </div>

              {/* Bottom tab navigation */}
              <TabNavigation />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Technical Tab */}
        <TabsContent value="technical">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Technical Information
              </CardTitle>
              <CardDescription>DNO applications and system details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* DNO Application */}
                <div>
                  <h3 className="text-sm font-medium mb-3">DNO Application</h3>
                  <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">DNO Provider</h4>
                      <InlineSelectField
                        value={installation.dnoProvider}
                        options={[
                          { value: 'UK Power Networks', label: 'UK Power Networks' },
                          { value: 'Northern Powergrid', label: 'Northern Powergrid' },
                          { value: 'Scottish Power', label: 'Scottish Power' },
                          { value: 'SSE Networks', label: 'SSE Networks' },
                          { value: 'Western Power Distribution', label: 'Western Power Distribution' },
                          { value: 'Electricity North West', label: 'Electricity North West' },
                        ]}
                        onSave={(value) => handleFieldUpdate('dnoProvider', value)}
                        placeholder="Select DNO provider"
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Application Type
                      </h4>
                      <InlineSelectField
                        value={installation.dnoApplicationType}
                        options={[
                          { value: 'G98', label: 'G98 (Fast Track)' },
                          { value: 'G99', label: 'G99 (Standard)' },
                        ]}
                        onSave={(value) => handleFieldUpdate('dnoApplicationType', value)}
                        placeholder="Select application type"
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Application Sent
                      </h4>
                      <InlineDateField
                        value={installation.dnoApplicationSent ? new Date(installation.dnoApplicationSent) : null}
                        onSave={(value) => handleFieldUpdate('dnoApplicationSent', value)}
                        placeholder="Click to set date"
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">ENA Reference</h4>
                      <InlineTextField
                        value={installation.dnoSystemReference}
                        onSave={(value) => handleFieldUpdate('dnoSystemReference', value)}
                        placeholder="Click to add ENA reference"
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        DNO Case Reference
                      </h4>
                      <InlineTextField
                        value={installation.dnoCaseReference}
                        onSave={(value) => handleFieldUpdate('dnoCaseReference', value)}
                        placeholder="Click to add case reference"
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        DNO Approval Date
                      </h4>
                      <InlineDateField
                        value={installation.dnoApproved ? new Date(installation.dnoApproved) : null}
                        onSave={(value) => handleFieldUpdate('dnoApproved', value)}
                        placeholder="Click to set approval date"
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Fast Track Reference
                      </h4>
                      <InlineTextField
                        value={installation.fastTrackReference}
                        onSave={(value) => handleFieldUpdate('fastTrackReference', value)}
                        placeholder="Click to add reference"
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        DNO Offer Letter Received
                      </h4>
                      <InlineDateField
                        value={installation.dnoOfferLetterReceived ? new Date(installation.dnoOfferLetterReceived) : null}
                        onSave={(value) => handleFieldUpdate('dnoOfferLetterReceived', value)}
                        placeholder="Click to set date"
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Docusign Status
                      </h4>
                      <InlineSelectField
                        value={installation.dnoDocusignStatus || ''}
                        options={[
                          { value: '', label: 'Not Started' },
                          { value: 'pending', label: 'Pending' },
                          { value: 'sent', label: 'Sent' },
                          { value: 'signed', label: 'Signed' },
                        ]}
                        onSave={(value) => handleFieldUpdate('dnoDocusignStatus', value)}
                        placeholder="Select status"
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        DNO Fee Paid
                      </h4>
                      <InlineSelectField
                        value={installation.dnoFeePaid ? 'Yes' : 'No'}
                        options={[
                          { value: 'Yes', label: 'Yes' },
                          { value: 'No', label: 'No' },
                        ]}
                        onSave={(value) => handleFieldUpdate('dnoFeePaid', value === 'Yes')}
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Limit Added to Survey
                      </h4>
                      <InlineSelectField
                        value={installation.dnoLimitAddedToSurvey ? 'Yes' : 'No'}
                        options={[
                          { value: 'Yes', label: 'Yes' },
                          { value: 'No', label: 'No' },
                        ]}
                        onSave={(value) => handleFieldUpdate('dnoLimitAddedToSurvey', value === 'Yes')}
                      />
                    </div>
                  </div>
                </div>

                {/* System Technical Details */}
                <div>
                  <h3 className="text-sm font-medium mb-3">System Technical Details</h3>
                  <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">MPAN Number</h4>
                      <InlineTextField
                        value={installation.mpanNumber}
                        onSave={(value) => handleFieldUpdate('mpanNumber', value)}
                        placeholder="Click to add MPAN"
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Export MPAN</h4>
                      <InlineTextField
                        value={installation.exportMpan}
                        onSave={(value) => handleFieldUpdate('exportMpan', value)}
                        placeholder="Click to add export MPAN"
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Inverter Serial Number
                      </h4>
                      <InlineTextField
                        value={installation.inverterSerialNumber}
                        onSave={(value) => handleFieldUpdate('inverterSerialNumber', value)}
                        placeholder="Click to add serial number"
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Battery Serial Number
                      </h4>
                      <InlineTextField
                        value={installation.batterySerialNumber}
                        onSave={(value) => handleFieldUpdate('batterySerialNumber', value)}
                        placeholder="Click to add serial number"
                      />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Commissioning Date
                      </h4>
                      <InlineDateField
                        value={installation.commissioningDate ? new Date(installation.commissioningDate) : null}
                        onSave={(value) => handleFieldUpdate('commissioningDate', value)}
                        placeholder="Click to set commissioning date"
                      />
                    </div>
                  </div>
                </div>

                {/* Battery Serial Numbers Section - Added in Technical Details */}
                <div className="mt-6">
                  <h3 className="text-sm font-medium mb-1">Battery Serial Number</h3>
                  <p className="text-muted-foreground mb-3">
                    {installation.batterySerialNumber || 'No battery serial numbers recorded'}
                  </p>

                  <h3 className="text-sm font-medium mb-2">Additional Battery Details</h3>
                  <BatterySerialNumbers installationId={installation.id} />
                </div>
              </div>

              {/* Bottom tab navigation */}
              <TabNavigation />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab - Main document repository with enhanced section headers */}
        <TabsContent value="completion-records">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="h-5 w-5" />
                Completion of Works Records
              </CardTitle>
              <CardDescription>
                Manage completion of works records for customer sign-off
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isErrorInstallation ? (
                <div className="py-8 text-center">
                  <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">
                    Error loading installation data:{' '}
                    {(installationError as Error)?.message || 'Unknown error'}
                  </p>
                  <Button
                    onClick={() =>
                      queryClient.invalidateQueries({ queryKey: ['/api/installations', id] })
                    }
                  >
                    Retry
                  </Button>
                </div>
              ) : installation ? (
                <CompletionOfWorksRecords
                  installationId={id}
                  installation={installation}
                  customer={customer}
                />
              ) : (
                <div className="py-8 text-center">
                  <p className="text-muted-foreground mb-4">
                    Unable to load completion of works records. Please try refreshing the page.
                  </p>
                  <Button
                    onClick={() =>
                      queryClient.invalidateQueries({ queryKey: ['/api/installations', id] })
                    }
                  >
                    Refresh Data
                  </Button>
                </div>
              )}

              {/* Bottom tab navigation */}
              <TabNavigation />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Documents & Certificates
              </CardTitle>
              <CardDescription>Manage installation documents and certificates</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Document preview grid */}
              <div className="mb-8">
                <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
                  <FileArchive className="h-5 w-5 text-primary" />
                  All Documents
                </h3>
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                  {/* Combine all document arrays and show the most recent documents */}
                  {(() => {
                    // Extract all documents from different categories
                    const allDocuments = [
                      ...(installation.technicalDocuments || []),
                      ...(installation.financeDocuments || []),
                      ...(installation.dnoDocuments || []),
                      ...(installation.contractDocuments || []),
                      ...(installation.postInstallDocuments || []),
                      ...(installation.handoverDocuments || []),
                      ...(installation.maintainingDocuments || []),
                      ...(installation.onsiteDocuments || []),
                    ].filter((doc) => doc); // Filter out any undefined/null values

                    // If no documents found
                    if (allDocuments.length === 0) {
                      return (
                        <div className="col-span-full p-5 bg-muted/50 rounded-md border border-dashed border-muted-foreground/30 text-center">
                          <p className="text-muted-foreground">
                            No documents have been uploaded yet
                          </p>
                          <Button
                            variant="link"
                            onClick={() => navigate(`/installations/${id}/edit`)}
                          >
                            Upload documents
                          </Button>
                        </div>
                      );
                    }

                    // Display all documents up to 6
                    return allDocuments.slice(0, 6).map((doc, index) => {
                      // Determine document type for icon
                      let IconComponent = FileText;
                      if (doc.type?.startsWith('image/')) {
                        IconComponent = ImageIcon;
                      }

                      return (
                        <div
                          key={index}
                          className="p-4 border rounded-md bg-white dark:bg-slate-900 hover:border-primary/50 transition-colors cursor-pointer"
                          onClick={() => {
                            // Check if this is an LOA document by looking at multiple indicators
                            const isLoaDocument =
                              (doc as any).filePath?.includes('/letters-of-authority/') ||
                              (doc as any).filePath?.includes('/loa/') ||
                              doc.data?.includes('/letters-of-authority/') ||
                              doc.data?.includes('/loa/') ||
                              doc.data?.startsWith('/loa/') ||
                              (doc as any).isLOA ||
                              (doc as any).loaId ||
                              doc.id?.toString().startsWith('loa-') ||
                              doc.id?.toString().startsWith('loa_') ||
                              doc.name?.toLowerCase().includes('letter_of_authority') ||
                              doc.name?.toLowerCase().includes('letter of authority') ||
                              (doc as any).filename
                                ?.toLowerCase()
                                .includes('letter_of_authority') ||
                              (doc as any).filename
                                ?.toLowerCase()
                                .includes('letter of authority') ||
                              (doc as any).filename?.includes('Letter_of_Authority_') ||
                              (doc as any).category === 'loa';

                            if (isLoaDocument) {
                              // Extract LOA ID from various possible sources
                              let loaId = '';

                              // Direct loaId property (most reliable)
                              if ((doc as any).loaId) {
                                loaId = (doc as any).loaId.toString();
                              }
                              // ID starting with loa-
                              else if (doc.id?.toString().startsWith('loa-')) {
                                loaId = doc.id.toString().replace('loa-', '');
                              }
                              // ID starting with loa_
                              else if (doc.id?.toString().startsWith('loa_')) {
                                loaId = doc.id.toString().replace('loa_', '');
                              }
                              // Extract from filePath like /loa/123
                              else if ((doc as any).filePath?.includes('/loa/')) {
                                const match = (doc as any).filePath.match(/\/loa\/(\d+)/);
                                if (match) {
                                  loaId = match[1];
                                }
                              }
                              // Extract from data URL like /loa/123
                              else if (
                                doc.data?.includes('/loa/') ||
                                doc.data?.startsWith('/loa/')
                              ) {
                                const match = doc.data.match(/\/loa\/(\d+)/);
                                if (match) {
                                  loaId = match[1];
                                }
                              }
                              // Extract from letters-of-authority path
                              else if (doc.data?.includes('/letters-of-authority/')) {
                                const match = doc.data.match(/\/letters-of-authority\/(\d+)/);
                                if (match) {
                                  loaId = match[1];
                                }
                              }
                              // Extract from filePath like /letters-of-authority/123
                              else if ((doc as any).filePath?.includes('/letters-of-authority/')) {
                                const match = (doc as any).filePath.match(
                                  /\/letters-of-authority\/(\d+)/
                                );
                                if (match) {
                                  loaId = match[1];
                                }
                              }
                              // Extract from filename pattern Letter_of_Authority_123
                              else if ((doc as any).filename?.includes('Letter_of_Authority_')) {
                                const match = (doc as any).filename.match(
                                  /Letter_of_Authority_(\d+)/
                                );
                                if (match) {
                                  loaId = match[1];
                                }
                              }
                              // Extract from name pattern
                              else if (doc.name?.includes('Letter of Authority')) {
                                // Try multiple patterns for LOA name
                                const patterns = [
                                  /Letter of Authority.*?-\s*(\d+)/i, // "Letter of Authority - 123"
                                  /Letter of Authority.*?(\d+)/i, // "Letter of Authority 123"
                                  /LOA.*?(\d+)/i, // "LOA 123"
                                  /Authority.*?(\d+)/i, // "Authority 123"
                                ];
                                for (const pattern of patterns) {
                                  const match = doc.name.match(pattern);
                                  if (match) {
                                    loaId = match[1];
                                    break;
                                  }
                                }
                              }

                              if (loaId) {
                                console.log('Opening LOA document with ID:', loaId);
                                navigate(`/loa/${loaId}`);
                                return;
                              } else {
                                console.warn('Could not extract LOA ID from document:', doc);
                              }
                            }

                            // For other documents, open in new tab
                            if (doc.data) {
                              window.open(doc.data, '_blank');
                            }
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <IconComponent className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                            <div className="overflow-hidden">
                              <p className="font-medium truncate" title={doc.name}>
                                {doc.name || 'Document'}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {doc.type?.split('/')[1]?.toUpperCase() || 'File'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Quick jump links to document sections */}
              <div className="mb-8 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  onClick={() =>
                    document.getElementById('finance-docs')?.scrollIntoView({ behavior: 'smooth' })
                  }
                >
                  <Banknote className="h-4 w-4" />
                  Finance
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  onClick={() =>
                    document.getElementById('dno-docs')?.scrollIntoView({ behavior: 'smooth' })
                  }
                >
                  <Zap className="h-4 w-4" />
                  DNO Application
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  onClick={() =>
                    document.getElementById('onsite-docs')?.scrollIntoView({ behavior: 'smooth' })
                  }
                >
                  <Construction className="h-4 w-4" />
                  On-site
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  onClick={() =>
                    document
                      .getElementById('technical-docs')
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }
                >
                  <Cpu className="h-4 w-4" />
                  Technical
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  onClick={() =>
                    document
                      .getElementById('postinstall-docs')
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Post-installation
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  onClick={() =>
                    document.getElementById('handover-docs')?.scrollIntoView({ behavior: 'smooth' })
                  }
                >
                  <FileCheck className="h-4 w-4" />
                  Handover
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  onClick={() =>
                    document.getElementById('contract-docs')?.scrollIntoView({ behavior: 'smooth' })
                  }
                >
                  <FileText className="h-4 w-4" />
                  Contract
                </Button>
              </div>

              <div className="space-y-12">
                {/* Finance Documents */}
                <div id="finance-docs" className="pt-2">
                  <div className="border-b pb-2 mb-4">
                    <h2 className="text-xl font-semibold flex items-center">
                      <Banknote className="h-5 w-5 mr-2 text-primary" />
                      Finance Documents
                    </h2>
                  </div>
                  <DocumentViewer
                    documents={installation.financeDocuments || []}
                    title=""
                    emptyMessage="No finance documents have been uploaded"
                  />
                </div>

                {/* DNO Documents */}
                <div id="dno-docs" className="pt-2">
                  <div className="border-b pb-2 mb-4">
                    <h2 className="text-xl font-semibold flex items-center">
                      <Zap className="h-5 w-5 mr-2 text-primary" />
                      DNO Application Documents
                    </h2>
                  </div>
                  <DocumentViewer
                    documents={installation.dnoDocuments || []}
                    title=""
                    emptyMessage="No DNO application documents have been uploaded"
                  />
                </div>

                {/* On-site Documents */}
                <div id="onsite-docs" className="pt-2">
                  <div className="border-b pb-2 mb-4">
                    <h2 className="text-xl font-semibold flex items-center">
                      <Construction className="h-5 w-5 mr-2 text-primary" />
                      On-site Documents
                    </h2>
                  </div>
                  <DocumentViewer
                    documents={installation.onsiteDocuments || []}
                    title=""
                    emptyMessage="No on-site documents have been uploaded"
                  />
                </div>

                {/* Technical Documents */}
                <div id="technical-docs" className="pt-2">
                  <div className="border-b pb-2 mb-4">
                    <h2 className="text-xl font-semibold flex items-center">
                      <Cpu className="h-5 w-5 mr-2 text-primary" />
                      Technical Documents
                    </h2>
                  </div>
                  <DocumentViewer
                    documents={installation.technicalDocuments || []}
                    title=""
                    emptyMessage="No technical documents have been uploaded"
                  />
                </div>

                {/* Post-installation Documents */}
                <div id="postinstall-docs" className="pt-2">
                  <div className="border-b pb-2 mb-4">
                    <h2 className="text-xl font-semibold flex items-center">
                      <CheckCircle2 className="h-5 w-5 mr-2 text-primary" />
                      Post-installation Documents
                    </h2>
                  </div>
                  <DocumentViewer
                    documents={installation.postInstallDocuments || []}
                    title=""
                    emptyMessage="No post-installation documents have been uploaded"
                  />
                </div>

                {/* Handover Documents */}
                <div id="handover-docs" className="pt-2">
                  <div className="border-b pb-3 mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <h2 className="text-xl font-semibold flex items-center">
                        <FileCheck className="h-5 w-5 mr-2 text-primary" />
                        Handover Documents
                      </h2>
                      <Button
                        size="sm"
                        onClick={() => handleSendHandoverDocuments(id)}
                        className="flex items-center gap-1"
                        disabled={isSendingHandoverDocs}
                        title={`Send handover documents`}
                      >
                        {isSendingHandoverDocs ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="h-4 w-4" />
                            Send Handover Documents
                          </>
                        )}
                      </Button>
                    </div>
                    {customer && customer.email ? (
                      <div className="text-xs text-muted-foreground flex items-center">
                        <Mail className="h-3 w-3 mr-1" />
                        Documents will be sent to:{' '}
                        <span className="font-medium ml-1">{customer.email}</span>
                      </div>
                    ) : (
                      <div className="text-xs text-amber-600 flex items-center">
                        <Mail className="h-3 w-3 mr-1" />
                        Will use installation-specific contact email if available
                      </div>
                    )}
                  </div>
                  <DocumentViewer
                    documents={installation.handoverDocuments || []}
                    title=""
                    emptyMessage="No handover documents have been uploaded"
                  />
                </div>

                {/* Contract Documents */}
                <div id="contract-docs" className="pt-2">
                  <div className="border-b pb-2 mb-4">
                    <h2 className="text-xl font-semibold flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      Contract Documents
                    </h2>
                  </div>
                  <DocumentViewer
                    documents={installation.contractDocuments || []}
                    title=""
                    emptyMessage="No contract documents have been uploaded"
                  />
                </div>
              </div>

              {/* Bottom tab navigation */}
              <TabNavigation />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
